import { useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/providers/AuthProvider';
import { useIsAdmin } from '@/hooks/useIsAdmin';
import { isOrderTrackingUser } from '@/config/features';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { ArrowLeft, Phone, User, Share2 } from 'lucide-react';
import { geocodeAddressCached } from '@/utils/osmGeocoding';


const SUPPORT_PHONE = '+2202554992';
const FALLBACK_COORDS = { lat: 13.4549, lng: -16.5790 }; // Serekunda, Gambia
const TILE_URL = 'https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png';
const TILE_OPTIONS: L.TileLayerOptions = {
  subdomains: 'abcd',
  maxZoom: 20,
  attribution: '&copy; OpenStreetMap contributors &copy; CARTO',
};

interface Order {
  id: string;
  customer_id: string;
  customer_name: string | null;
  restaurant_id: string | null;
  driver_id: string | null;
  status: string;
  delivery_address: string;
  delivery_type: string | null;
  restaurants?: { name: string } | null;
}

type Stage = { key: string; short: string; headline: (name: string, driver: string) => string };

const STAGES: Stage[] = [
  { key: 'accepted', short: 'Order accepted', headline: (n) => `${n} accepted your order` },
  { key: 'preparing', short: 'Preparing your order', headline: (n) => `${n} is preparing your order` },
  { key: 'pickup', short: 'Driver heading to pickup', headline: (_n, d) => `${d || 'Your driver'} is on the way to pick up your order` },
  { key: 'enroute', short: 'Your order is on its way', headline: (_n, d) => `${d || 'Your driver'} is on the way` },
  { key: 'arrived', short: 'Driver has arrived', headline: (_n, d) => `Meet ${d || 'your driver'} outside` },
  { key: 'completed', short: 'Order completed', headline: () => 'Your order has been completed 🎉' },
];

const currentIndex = (status: string, hasDriver: boolean): number => {
  switch (status) {
    case 'pending':
    case 'confirmed':
      return hasDriver ? 2 : 0;
    case 'preparing':
      return hasDriver ? 2 : 1;
    case 'ready':
    case 'ready_for_pickup':
      return 2;
    case 'picked_up':
      return 3;
    case 'arrived':
      return 4;
    case 'delivered':
    case 'completed':
      return 5;
    default:
      return 0;
  }
};

const OrderTrackingPage = () => {
  const { orderId } = useParams<{ orderId: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { isAdmin, loading: adminLoading } = useIsAdmin();

  const [order, setOrder] = useState<Order | null>(null);
  const [driverProfile, setDriverProfile] = useState<any>(null);
  const [customerCoords, setCustomerCoords] = useState<{ lat: number; lng: number } | null>(null);
  const [, setDriverCoords] = useState<{ lat: number; lng: number } | null>(null);
  const [helpOpen, setHelpOpen] = useState(false);
  const [mapReady, setMapReady] = useState(false);
  
  const [error, setError] = useState<string | null>(null);
  const [stageTimestamps, setStageTimestamps] = useState<Record<number, number>>({});

  const mapRef = useRef<HTMLDivElement>(null);
  const mapInstance = useRef<L.Map | null>(null);
  const driverMarkerRef = useRef<L.Marker | null>(null);
  const customerMarkerRef = useRef<L.Marker | null>(null);
  const routeRef = useRef<L.Polyline | null>(null);
  const fitDone = useRef(false);
  const customerLookupKeyRef = useRef('');

  // Load persisted stage timestamps
  useEffect(() => {
    if (!orderId) return;
    try {
      const raw = localStorage.getItem(`order-track-stages-${orderId}`);
      if (raw) setStageTimestamps(JSON.parse(raw));
    } catch { /* ignore */ }
  }, [orderId]);

  const formatTime = (ts: number) =>
    new Date(ts).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' }).toLowerCase();


  // Access gate
  useEffect(() => {
    if (adminLoading) return;
    if (!user) {
      navigate('/auth');
      return;
    }
    if (!isOrderTrackingUser(user.email, isAdmin)) {
      setError('This feature is not available for your account yet.');
    }
  }, [user, isAdmin, adminLoading, navigate]);

  // Load order + realtime + polling fallback
  useEffect(() => {
    if (!orderId) return;
    if (!user) return; // wait until auth session is hydrated so RLS lets the row through
    let cancelled = false;

    const load = async () => {
      const { data, error: err } = await supabase
        .from('orders')
        .select('id, customer_id, customer_name, restaurant_id, driver_id, status, delivery_address, delivery_type, live_location, restaurants(name)')
        .eq('id', orderId)
        .maybeSingle();
      if (cancelled) return;
      if (err) {
        setError('Order not found');
        return;
      }
      if (!data) {
        // Row exists but might be blocked by a transient RLS/session issue — don't lock into an error state
        return;
      }
      setError(null);
      setOrder((prev) => {
        if (prev && prev.status === (data as any).status && prev.driver_id === (data as any).driver_id) {
          return { ...prev, ...(data as any) };
        }
        return data as any;
      });
    };
    load();

    const channel = supabase
      .channel(`order-track-${orderId}`)
      .on(
        'postgres_changes',
        { event: 'UPDATE', schema: 'public', table: 'orders', filter: `id=eq.${orderId}` },
        (payload) => {
          setOrder((prev) => (prev ? { ...prev, ...(payload.new as any) } : (payload.new as any)));
        },
      )
      .subscribe();

    const poll = setInterval(load, 5000);

    return () => {
      cancelled = true;
      clearInterval(poll);
      supabase.removeChannel(channel);
    };
  }, [orderId, user?.id]);

  // Driver info
  useEffect(() => {
    if (!order?.driver_id) {
      setDriverProfile(null);
      return;
    }
    (async () => {
      const [{ data: profile }, { data: dp }] = await Promise.all([
        supabase.from('profiles').select('full_name, avatar_url, phone').eq('id', order.driver_id!).maybeSingle(),
        supabase.from('driver_profiles').select('vehicle_plate, vehicle_type, rating').eq('user_id', order.driver_id!).maybeSingle(),
      ]);
      setDriverProfile({ ...(profile || {}), ...(dp || {}) });
    })();
  }, [order?.driver_id]);

  // Prefer stored pin coordinates from the order; fall back to geocoding the address
  useEffect(() => {
    const loc: any = (order as any)?.live_location;
    const pinLat = loc?.pin_lat ?? loc?.latitude;
    const pinLng = loc?.pin_lng ?? loc?.longitude;
    const lookupKey = `${order?.id || ''}:${pinLat || ''}:${pinLng || ''}:${order?.delivery_address || ''}`;

    if (!order || customerLookupKeyRef.current === lookupKey) return;
    customerLookupKeyRef.current = lookupKey;

    if (pinLat != null && pinLng != null) {
      setCustomerCoords({ lat: Number(pinLat), lng: Number(pinLng) });
      return;
    }

    // Never hold the map behind geocoding. Show the normal app map immediately,
    // then move the marker if address lookup succeeds.
    setCustomerCoords(FALLBACK_COORDS);

    if (!order?.delivery_address) {
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const c = await Promise.race([
          geocodeAddressCached(order.delivery_address),
          new Promise<null>((resolve) => setTimeout(() => resolve(null), 6000)),
        ]);
        if (cancelled) return;
        if (c && (c as any).latitude != null) {
          setCustomerCoords({ lat: (c as any).latitude, lng: (c as any).longitude });
        }
      } catch {
        // Keep the already-visible fallback map.
      }
    })();
    return () => { cancelled = true; };
  }, [order?.id, order?.delivery_address, (order as any)?.live_location]);

  // Init map
  useEffect(() => {
    if (!mapRef.current || mapInstance.current || !customerCoords) return;
    const el = mapRef.current;
    setMapReady(false);
    const map = new L.Map(el, {
      center: [customerCoords.lat, customerCoords.lng],
      zoom: 14,
      zoomControl: false,
      attributionControl: false,
    });
    mapInstance.current = map;

    new L.TileLayer(TILE_URL, TILE_OPTIONS).addTo(map);

    // Some mobile browsers report 0 height for a flex child on first paint.
    // Retry invalidateSize a few times until the container has real dimensions.
    const invalidate = () => map.invalidateSize();
    const timers = [50, 200, 500, 1000, 2000].map((ms) => setTimeout(invalidate, ms));
    const readyTimer = setTimeout(() => {
      invalidate();
      setMapReady(true);
    }, 250);
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(invalidate) : null;
    ro?.observe(el);
    (map as any).__cleanup = () => {
      timers.forEach(clearTimeout);
      clearTimeout(readyTimer);
      ro?.disconnect();
    };
  }, [customerCoords]);


  useEffect(() => () => {
    const m: any = mapInstance.current;
    if (m?.__cleanup) m.__cleanup();
    mapInstance.current?.remove();
    mapInstance.current = null;
  }, []);


  // Customer marker
  useEffect(() => {
    const map = mapInstance.current;
    if (!map || !customerCoords) return;

    const homeIcon = L.divIcon({
      html: `<div style="width:32px;height:32px;background:#dc2626;border-radius:50% 50% 50% 0;transform:rotate(-45deg);border:3px solid white;box-shadow:0 4px 12px rgba(0,0,0,0.25);display:flex;align-items:center;justify-content:center;">
        <div style="transform:rotate(45deg);color:white;font-size:14px;">📍</div>
      </div>`,
      className: '',
      iconSize: [32, 32],
      iconAnchor: [16, 32],
    });

    if (!customerMarkerRef.current) {
      customerMarkerRef.current = L.marker([customerCoords.lat, customerCoords.lng], { icon: homeIcon }).addTo(map);
    } else {
      customerMarkerRef.current.setLatLng([customerCoords.lat, customerCoords.lng]);
    }

    if (!fitDone.current) {
      map.setView([customerCoords.lat, customerCoords.lng], map.getZoom());
    }
  }, [customerCoords]);

  // Driver location + route
  useEffect(() => {
    const map = mapInstance.current;
    if (!map || !order?.driver_id || !customerCoords) return;

    let cancelled = false;
    let lastRouteKey = '';

    const carIcon = L.divIcon({
      html: `<div style="width:32px;height:32px;background:white;border-radius:50%;border:3px solid #dc2626;box-shadow:0 4px 12px rgba(0,0,0,0.3);display:flex;align-items:center;justify-content:center;font-size:16px;">🛵</div>`,
      className: '',
      iconSize: [32, 32],
      iconAnchor: [16, 16],
    });

    let animFrame: number | null = null;

    const animateMarkerTo = (fromLat: number, fromLng: number, toLat: number, toLng: number) => {
      if (animFrame) cancelAnimationFrame(animFrame);
      const start = performance.now();
      const duration = 2500;
      const step = (now: number) => {
        const t = Math.min(1, (now - start) / duration);
        const ease = t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t;
        const lat = fromLat + (toLat - fromLat) * ease;
        const lng = fromLng + (toLng - fromLng) * ease;
        driverMarkerRef.current?.setLatLng([lat, lng]);
        if (t < 1) animFrame = requestAnimationFrame(step);
      };
      animFrame = requestAnimationFrame(step);
    };

    const tick = async () => {
      const { data: loc } = await supabase
        .from('driver_profiles')
        .select('current_latitude, current_longitude')
        .eq('user_id', order.driver_id!)
        .maybeSingle();

      if (cancelled || !loc?.current_latitude || !loc?.current_longitude) return;
      const dLat = Number(loc.current_latitude);
      const dLng = Number(loc.current_longitude);

      if (!driverMarkerRef.current) {
        driverMarkerRef.current = L.marker([dLat, dLng], { icon: carIcon }).addTo(map);
        setDriverCoords({ lat: dLat, lng: dLng });
      } else {
        const prev = driverMarkerRef.current.getLatLng();
        // Skip micro-jitter (<5m)
        const dLatDeg = dLat - prev.lat;
        const dLngDeg = dLng - prev.lng;
        const roughMeters = Math.sqrt(dLatDeg * dLatDeg + dLngDeg * dLngDeg) * 111000;
        if (roughMeters < 5) return;
        animateMarkerTo(prev.lat, prev.lng, dLat, dLng);
        setDriverCoords({ lat: dLat, lng: dLng });
      }

      if (!fitDone.current) {
        map.fitBounds(
          L.latLngBounds([[dLat, dLng], [customerCoords.lat, customerCoords.lng]]),
          { padding: [50, 40] },
        );
        fitDone.current = true;
      }

      const key = `${dLat.toFixed(3)},${dLng.toFixed(3)}`;
      if (key === lastRouteKey) return;
      lastRouteKey = key;

      try {
        const res = await fetch(
          `https://router.project-osrm.org/route/v1/driving/${dLng},${dLat};${customerCoords.lng},${customerCoords.lat}?overview=full&geometries=geojson`,
        );
        const j = await res.json();
        const coords = j?.routes?.[0]?.geometry?.coordinates;
        if (coords?.length) {
          const latlngs: L.LatLngExpression[] = coords.map(([lng, lat]: number[]) => [lat, lng]);
          if (routeRef.current) routeRef.current.setLatLngs(latlngs);
          else routeRef.current = L.polyline(latlngs, { color: '#dc2626', weight: 5, opacity: 0.9 }).addTo(map);
        }
      } catch { /* ignore */ }
    };

    tick();
    const interval = setInterval(tick, 4000);
    return () => {
      cancelled = true;
      clearInterval(interval);
      if (animFrame) cancelAnimationFrame(animFrame);
    };
  }, [order?.driver_id, customerCoords]);

  const restaurantName = order?.restaurants?.name || 'The store';
  const driverName = driverProfile?.full_name?.split(' ')[0] || '';
  const hasDriver = !!order?.driver_id;
  const idx = order ? currentIndex(order.status, hasDriver) : 0;
  const isDelivered = order?.status === 'delivered' || order?.status === 'completed';
  const stage = STAGES[idx] || STAGES[0];
  const headline = useMemo(() => stage.headline(restaurantName, driverName), [stage, restaurantName, driverName]);
  const showDriverCard = hasDriver && !isDelivered;

  // Persist the timestamp the first time we see each stage so it stays fixed.
  useEffect(() => {
    if (!orderId || !order) return;
    if (stageTimestamps[idx]) return;
    const next = { ...stageTimestamps, [idx]: Date.now() };
    setStageTimestamps(next);
    try {
      localStorage.setItem(`order-track-stages-${orderId}`, JSON.stringify(next));
    } catch { /* ignore */ }
  }, [idx, orderId, order, stageTimestamps]);

  const stageTimeLabel = stageTimestamps[idx] ? formatTime(stageTimestamps[idx]) : '';

  const shareTracking = async () => {
    const url = `${window.location.origin}/track-order/${orderId}`;
    if (navigator.share) {
      try { await navigator.share({ title: 'Track my GAMBGO order', url }); } catch { /* noop */ }
    } else {
      navigator.clipboard.writeText(url);
    }
  };

  if (error) {
    return (
      <div className="min-h-screen bg-white flex flex-col items-center justify-center p-6 text-center">
        <p className="text-gray-700 mb-4">{error}</p>
        <Button onClick={() => navigate('/order-history')} className="bg-red-600 hover:bg-red-700">
          Back to orders
        </Button>
      </div>
    );
  }

  if (!order) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-600" />
      </div>
    );
  }

  return (
    <div className="h-screen w-full flex flex-col bg-white overflow-hidden">
      {/* Header */}
      <div className="flex-shrink-0 px-4 pt-4 pb-2 flex items-center justify-between">
        <button
          onClick={() => navigate(-1)}
          className="h-10 w-10 -ml-2 flex items-center justify-center"
          aria-label="Back"
        >
          <ArrowLeft className="h-6 w-6 text-gray-900" />
        </button>
        <div className="flex items-center gap-1">
          <button
            onClick={() => setHelpOpen(true)}
            className="px-3 h-9 text-sm font-semibold text-red-600"
          >
            NEED HELP?
          </button>
          <button
            onClick={shareTracking}
            className="h-10 w-10 flex items-center justify-center"
            aria-label="Share"
          >
            <Share2 className="h-5 w-5 text-gray-900" />
          </button>
        </div>
      </div>

      {/* Greeting + status */}
      <div className="flex-shrink-0 px-5 pt-2 pb-4">
        <div className="flex items-start justify-between gap-3">
          <div className="flex-1 min-w-0">
            <h1 className="text-2xl font-bold text-gray-900 leading-tight">
              Hi {order.customer_name?.split(' ')[0] || 'there'},
            </h1>
            <p key={headline} className="text-base text-gray-700 mt-1 animate-in fade-in slide-in-from-bottom-1 duration-300">
              {headline}
            </p>
          </div>
          <div className="text-3xl leading-none flex-shrink-0" aria-hidden>
            {idx <= 1 ? '🍲' : idx <= 3 ? '🛵' : idx === 4 ? '🏠' : '🎉'}
          </div>
        </div>

        {/* Segmented progress bar */}
        <div className="mt-4 flex gap-1.5">
          {STAGES.map((s, i) => {
            const active = isDelivered || i <= idx;
            return (
              <div
                key={s.key}
                className={`h-2 flex-1 rounded-full transition-colors duration-500 ${
                  active ? 'bg-red-600' : 'bg-gray-200'
                }`}
              />
            );
          })}
        </div>

        {/* Timestamp + short step (locked when stage first reached) */}
        <p className="mt-3 text-sm text-gray-500">
          {stageTimeLabel && (
            <>
              <span className="font-medium text-gray-700">{stageTimeLabel}</span>
              <span className="mx-1.5">–</span>
            </>
          )}
          <span>{isDelivered ? 'Order completed' : stage.short}</span>
        </p>
      </div>

      {/* Driver card (above map, always visible) */}
      {showDriverCard && (
        <div className="flex-shrink-0 px-4 pb-3">
          <div className="bg-white border border-gray-100 rounded-2xl shadow-sm p-3 flex items-center gap-3">
            <div className="h-11 w-11 rounded-full overflow-hidden bg-red-50 border border-red-100 flex items-center justify-center flex-shrink-0">
              {driverProfile?.avatar_url ? (
                <img src={driverProfile.avatar_url} alt="" className="h-full w-full object-cover" />
              ) : (
                <User className="h-6 w-6 text-red-600" />
              )}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-gray-900 truncate">
                {driverProfile?.full_name || 'Your driver'}
              </p>
              <p className="text-xs text-gray-500 truncate">
                Your rider
                {driverProfile?.vehicle_plate ? ` • ${driverProfile.vehicle_plate}` : ''}
              </p>
            </div>
            {driverProfile?.phone && (
              <a
                href={`tel:${driverProfile.phone}`}
                className="h-10 px-4 rounded-full bg-red-600 text-white text-xs font-bold tracking-wide flex items-center gap-1.5"
              >
                <Phone className="h-4 w-4" />
                CALL RIDER
              </a>
            )}
          </div>
        </div>
      )}

      {/* Map — fills remaining, nothing overlapping */}
      <div className="relative flex-1 min-h-[320px] mx-4 mb-4 rounded-2xl overflow-hidden border border-gray-100 shadow-sm">
        <div ref={mapRef} data-tracking-map className="absolute inset-0 bg-gray-100" />
        {(!customerCoords || !mapReady) && (
          <div className="absolute inset-0 z-[1000] flex items-center justify-center bg-gray-100/70">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-600" />
          </div>
        )}
      </div>

      {/* Help modal */}
      <Dialog open={helpOpen} onOpenChange={setHelpOpen}>
        <DialogContent className="max-w-sm rounded-2xl z-[10000]">
          <DialogHeader>
            <DialogTitle>Need help with your order?</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 pt-2">
            <a
              href={`tel:${SUPPORT_PHONE}`}
              className="flex items-center gap-3 rounded-xl border border-gray-200 p-3 hover:bg-gray-50"
            >
              <div className="h-10 w-10 rounded-full bg-red-600 flex items-center justify-center">
                <Phone className="h-5 w-5 text-white" />
              </div>
              <div>
                <p className="text-sm font-semibold text-gray-900">Call support</p>
                <p className="text-xs text-gray-500">{SUPPORT_PHONE}</p>
              </div>
            </a>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default OrderTrackingPage;
